import { Skeleton } from "../../../components/ui/skeleton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../../components/ui/dropdown-menu";
import { MoreVerticalIcon } from "lucide-react";
import { getRtrTag, useCloneSurveyMutation, useLazyGetSurveyShareLinkQuery, useUpdateSurveyMutation } from "../../../redux/survey/survey.endpoints";
import { useDispatch, useSelector } from "react-redux";
import React, { useEffect, useRef } from "react";
import { showNotification } from "../../../redux/notification/notification.slice";
import {
  Dialog,
  DialogContent,
  DialogDescription, DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../../components/ui/dialog";
// import { canEditSurvey } from "../../../redux/survey/survey.helpers";
import { useDashboardSurveyList } from "./useDashboardSurveyList";
import { Button } from "../../../components/ui/button";
import { Survey } from "../../../redux/survey/survey.types";
import { generateStandaloneSurveyLink } from "src/services/surveys/surveys.helpers";
import useShareRTR from "src/hooks/useShareRTR";
import { useNavigate } from "react-router-dom";
import { RadioButtonGrid, RadioButtonGridType } from "src/components/radio-button/radio-button";

type SurveyCardMenuProps = {
  readonly survey?: Survey;
};
const SurveyCardMenu = ({ survey }: SurveyCardMenuProps) => {
  const [getShareLink, { data: lazyLink, isFetching: isLinkFetching }] = useLazyGetSurveyShareLinkQuery();
  const [cloneSurvey, {data: clonedSurvey, isSuccess}] = useCloneSurveyMutation();
  const [updateSurvey] = useUpdateSurveyMutation();
  const [showMakeLiveDialog, setShowMakeLiveDialog] = React.useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = React.useState(false);

  const [makeLive, setMakeLive] = React.useState(false);
  const [showRtrCopyDialog, setShowRtrCopyDialog] = React.useState(false);
  const [rtrSize, setRtrSize] = React.useState<'300x250' | '970x250'>('300x250');
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const shouldCopyLink = useRef(false);
  const { onSurveyDeletion } = useDashboardSurveyList();

  const isSuperAdmin = useSelector((state: any) => state.userState.isSuperAdmin);
  const { onRTRShare } = useShareRTR({ surveyId: survey?.extSurveyId ?? "" });

  useEffect(() => {
    if (!lazyLink || isLinkFetching || !shouldCopyLink.current) {
      return;
    }

    void copyToClipboard(lazyLink);
  }, [lazyLink, isLinkFetching]);

  useEffect(() => {
    if (isSuccess && clonedSurvey?.extSurveyId) {
      navigate(`/create-survey?ext-survey-id=${clonedSurvey.extSurveyId}`);
    }
  }, [isSuccess, clonedSurvey, navigate]);

  if (!survey) {
    return (
      <Skeleton className="size-6 rounded-full bg-white/50" />
    );
  }

  // const canDeleteSurvey = canEditSurvey(survey.surveyStatus);

  const copyToClipboard = async(text: string) => {
    shouldCopyLink.current = false;
    await navigator.clipboard.writeText(text);
    dispatch(showNotification({ summary: 'Successfully copied to clipboard', theme: 'success' }));
  };

  const onCopy = () => {
    // This looks to be related to the app, not anything else (can most likely remove)
    shouldCopyLink.current = true;
    void getShareLink({ ext_survey_id: survey.extSurveyId });
  };

  const onWebSurveyLink = () => {
    copyToClipboard(generateStandaloneSurveyLink(survey.extSurveyId));
  };

  const onRTRCopy = async() => {
    setShowRtrCopyDialog(false);

    try {
      if (makeLive) {
        await updateSurvey({
          title: survey.title,
          survey_status: 'live',
          ext_survey_id: survey.extSurveyId,
        });
      }

      const rtrTag = await getRtrTag(survey.extSurveyId, false, rtrSize);
      if (!rtrTag || rtrTag.includes('"item": null')) {
        throw new Error('Error fetching RTR tag');
      }
      copyToClipboard(rtrTag);
    } catch (error) {
      dispatch(showNotification({ summary: 'Error fetching RTR tag', theme: 'warning' }));
    }
  };

  const onClone = () => {
    cloneSurvey({extSurveyId: survey.extSurveyId});
  };

  const onDialogDeleteSelected = (event: React.MouseEvent<HTMLButtonElement>) => {
    setShowDeleteDialog(false);
    onSurveyDeletion(survey.extSurveyId);
  };

  return (
    <Dialog>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <div className="flex justify-center items-center size-6 rounded-full bg-white/50 hover:bg-white focus:transparent">
            <MoreVerticalIcon className="size-4 fill-gray-600" />
          </div>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          side="bottom"
          align="end"
          onClick={(e) => e.stopPropagation()}
        >

          <DropdownMenuItem
            className="cursor-pointer hover:bg-gray-200"
            onSelect={onWebSurveyLink}
          >
            Copy web survey link
          </DropdownMenuItem>
          <DialogTrigger asChild>
            <DropdownMenuItem
              onSelect={() => {
                setShowRtrCopyDialog(true);
              }}
              className="cursor-pointer"
            >
              Copy RTR Tag code
            </DropdownMenuItem>
          </DialogTrigger>
          <DropdownMenuItem
            className="cursor-pointer hover:bg-gray-200"
            onSelect={onRTRShare}
          >
            Copy RTR preview link
          </DropdownMenuItem>

          <DropdownMenuItem
            className="cursor-pointer"
            onSelect={onClone}
          >
            Clone
          </DropdownMenuItem>
          <DialogTrigger asChild>
            <DropdownMenuItem
              onSelect={() => setShowDeleteDialog(true)}
              className="cursor-pointer"
            >
              Delete
            </DropdownMenuItem>
          </DialogTrigger>
        </DropdownMenuContent>
      </DropdownMenu>

      {showDeleteDialog && (
        <DialogContent
          onClick={e => e.stopPropagation()}
          className="bg-white"
        >
          <DialogHeader>
            <DialogTitle>
              Delete survey
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this survey? This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="submit"
              onClick={onDialogDeleteSelected}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      )}

      {showRtrCopyDialog && (
        <DialogContent
          onClick={e => e.stopPropagation()}
          className="bg-white"
        >
          <DialogHeader>
            <DialogTitle>
              Generate RTR
            </DialogTitle>
            <small>
              Select the size of the RTR to generate
            </small>
          </DialogHeader>
          <RadioButtonGrid
            buttons={[{ label: '300x250', value: '300x250' }, { label: '970x250', value: '970x250' }]}
            extSurveyId={survey?.extSurveyId}
            initialValue={rtrSize}
            onClick={() => setRtrSize(rtrSize === '300x250' ? '970x250' : '300x250')}
            title="Dimensions"
            type={RadioButtonGridType.RtrSize}
          />
          {survey?.surveyStatus === 'draft' ? (
            <div>
              <div className="flex flex-col-reverse sm:flex-row sm:justify-end">
                <small>
                  Survey is in draft. To recieve answers, set to live.
                </small>
              </div>

              <div className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2">
                <Button
                  variant="outline"
                  className="hover:bg-green-400"
                  type="submit"
                  onClick={() => {setMakeLive(true); onRTRCopy();}}
                >
                  Live
                </Button>
                <Button
                  className="bg-gray-600 hover:bg-red-500"
                  type="submit"
                  onClick={() => {setMakeLive(false); onRTRCopy();}}
                >
                  Draft
                </Button>

              </div>


            </div>
          ) : (
            <DialogFooter>
              <Button
                className="bg-gray-800 hover:bg-gray-900"
                type="submit"
                onClick={() => onRTRCopy()}
              >
                Submit
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      )}
    </Dialog>
  );
};

export default SurveyCardMenu;
